import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import jsQR from "jsqr";

import "./controller.css";
import TouchpadView from "./components/Touchpadview";

// ============================================================
// CONFIG
// ============================================================

const PORT = 8765;

// Socket.IO event name the receiver (TV / Electron) listens on.
// The payload is the same JSON object you used before: { action, ...extra }
const EVENT_NAME = "controller:message";

// ---- Room API (used when the scanned QR contains a roomId) ----
const API_BASE = "https://api07.atttic.com";
const DEVICE_NAME = "web controller";

// After the REST join, the controller attaches a Socket.IO connection to the
// API server and emits touch / click events on this event name.
//   emit("mobile:connect", { roomId, memberId, memberToken }, ack)
//   emit("controller:event", { action, mode, dx, dy, ... })
const ROOM_SOCKET_URL = API_BASE;
const ROOM_EVENT = "controller:event";

// ============================================================
// MAIN
// ============================================================

export default function App() {
  const [ip, setIp] = useState("192.168.0.244");

  const [connected, setConnected] = useState(false);
  const [connectionType, setConnectionType] = useState("tv"); // "electron" | "tv" | "room"

  const [tab, setTab] = useState("connect"); // "connect" | "rotate" | "mouse"

  const [scannerVisible, setScannerVisible] = useState(false);
  const hasScannedRef = useRef(false);
  const [scanError, setScanError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [personaliseText, setPersonaliseText] = useState("");

  const socketRef = useRef(null);
  const connectionId = useRef(0);

  const [room, setRoom] = useState(null); // { roomId, memberId, memberToken }
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState("");

  // Always holds the latest joined room (read by the event sender)
  const roomRef = useRef(null);
  roomRef.current = room;

  // ==========================================================
  // DISCONNECT
  // ==========================================================

  const disconnect = () => {
    console.log("[Controller] Disconnecting...");

    connectionId.current += 1;

    if (socketRef.current) {
      try {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      } catch {}

      socketRef.current = null;
    }

    setConnected(false);
    setRoom(null);
  };

  // ==========================================================
  // CONNECT (Socket.IO — used for both Electron and Android TV)
  // ==========================================================

  const connectSocket = (address, label, options = {}) => {
    disconnect();

    const currentConnection = ++connectionId.current;

    console.log(`[Controller] Connecting to ${label} ${address}`);

    try {
      const socket = io(address, {
        transports: ["websocket"],
        reconnection: false,
        timeout: 5000,
        ...options,
      });

      socketRef.current = socket;

      socket.on("connect", () => {
        if (currentConnection !== connectionId.current) {
          socket.disconnect();
          return;
        }

        console.log(`[Controller] ${label} connected`);

        setConnected(true);
      });

      socket.on(EVENT_NAME, (message) => {
        if (currentConnection !== connectionId.current) return;

        console.log(`[Controller] ${label}:`, message);
      });

      socket.on("connect_error", (error) => {
        if (currentConnection !== connectionId.current) return;

        console.log(
          `[Controller] ${label} connection error:`,
          error?.message || error,
        );

        setConnected(false);

        if (socketRef.current === socket) {
          socketRef.current = null;
        }
      });

      socket.on("disconnect", () => {
        if (currentConnection !== connectionId.current) return;

        console.log(`[Controller] ${label} disconnected`);

        setConnected(false);

        if (socketRef.current === socket) {
          socketRef.current = null;
        }
      });
    } catch (error) {
      console.log(`[Controller] ${label} connection failed:`, error);

      setConnected(false);
    }
  };

  const connectElectron = () =>
    connectSocket(`http://${ip.trim()}:${PORT}`, "Electron");

  const connectTV = (overrideIp) =>
    connectSocket(`http://${(overrideIp ?? ip).trim()}:${PORT}`, "Android TV");

  // ==========================================================
  // JOIN ROOM (POST /api/rooms/:roomId/join), then connect
  // ==========================================================

  const connectRoomSocket = (joined) => {
    const currentConnection = ++connectionId.current;

    console.log("[Controller] Connecting room socket:", ROOM_SOCKET_URL);

    const socket = io(ROOM_SOCKET_URL, {
      transports: ["websocket"],
      reconnection: false,
      timeout: 5000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      if (currentConnection !== connectionId.current) {
        socket.disconnect();
        return;
      }

      // Attach this socket to the member that just joined over REST
      socket.emit(
        "mobile:connect",
        {
          roomId: joined.roomId,
          memberId: joined.memberId,
          memberToken: joined.memberToken,
        },
        (res) => {
          if (currentConnection !== connectionId.current) return;

          if (res && res.ok) {
            console.log("[Controller] Attached to room", joined.roomId);
            setConnected(true);
          } else {
            console.log("[Controller] Attach failed:", res?.error);
            setJoinError(`Could not connect: ${res?.error || "unknown error"}`);
            disconnect();
          }
        },
      );
    });

    socket.on("room:kiosk-disconnected", () => {
      console.log("[Controller] Kiosk disconnected (may reconnect shortly)");
    });

    socket.on("connect_error", (error) => {
      if (currentConnection !== connectionId.current) return;

      console.log("[Controller] Room socket error:", error?.message || error);

      setJoinError(`Could not connect: ${error?.message || error}`);
      setConnected(false);
      setRoom(null);

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    });

    socket.on("disconnect", () => {
      if (currentConnection !== connectionId.current) return;

      console.log("[Controller] Room socket disconnected");

      setConnected(false);
      setRoom(null);
      setJoinError("Disconnected from room. Scan the QR code again.");

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    });
  };

  const joinRoom = async (roomId, fallbackIp) => {
    disconnect();

    setJoining(true);
    setJoinError("");

    try {
      const url = `${API_BASE}/api/rooms/${encodeURIComponent(roomId)}/join`;

      console.log("[Controller] Joining room:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ deviceName: DEVICE_NAME }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data.memberToken) {
        throw new Error("Join response is missing memberToken");
      }

      const joined = {
        roomId: String(data.roomId || roomId),
        memberId: data.memberId,
        memberToken: data.memberToken,
      };

      console.log(
        "[Controller] Joined room:",
        joined.roomId,
        "members:",
        data.members,
      );

      setRoom(joined);

      connectRoomSocket(joined);
    } catch (error) {
      console.log("[Controller] Join failed:", error?.message || error);

      setJoinError(`Could not join room: ${error?.message || error}`);

      if (fallbackIp) {
        setIp(fallbackIp);
        setConnectionType("tv");
        connectTV(fallbackIp);
      }
    } finally {
      setJoining(false);
    }
  };

  // ==========================================================
  // CONNECT
  // ==========================================================

  const connect = () => {
    const host = ip.trim();

    if (!host) {
      console.log("[Controller] IP address is empty");

      return;
    }

    if (connectionType === "electron") {
      connectElectron();
    } else {
      connectTV();
    }
  };

  // ==========================================================
  // QR SCANNER
  // ==========================================================

  const openScanner = () => {
    hasScannedRef.current = false;
    setScanError("");
    setScannerVisible(true);
  };

  const closeScanner = () => {
    setScannerVisible(false);
  };

  const handleBarcodeScanned = (data) => {
    if (hasScannedRef.current) {
      return;
    }

    hasScannedRef.current = true;

    console.log("[Controller] QR scanned:", data);

    let scannedIp = null;
    let scannedRoomId = null;

    try {
      const parsed = JSON.parse(data);

      if (parsed && typeof parsed.ip === "string") {
        scannedIp = parsed.ip;
      }

      if (parsed && typeof parsed.roomId === "string") {
        scannedRoomId = parsed.roomId;
      }
    } catch {
      const trimmed = data.trim();

      if (/^\d{1,3}(\.\d{1,3}){3}$/.test(trimmed)) {
        scannedIp = trimmed;
      }
    }

    if (!scannedRoomId && !scannedIp) {
      console.log("[Controller] QR code did not contain a valid IP or room");
      hasScannedRef.current = false;
      return;
    }

    setScannerVisible(false);

    // Room QR -> POST /api/rooms/:roomId/join, then connect.
    // (If the join fails and the QR also had an IP, it falls back to the IP.)
    if (scannedRoomId) {
      setConnectionType("room");
      joinRoom(scannedRoomId, scannedIp);
      return;
    }

    setIp(scannedIp);
    setConnectionType("tv");

    connectTV(scannedIp);
  };

  // Camera + QR decode loop (runs while the scanner modal is open).
  // NOTE: getUserMedia needs HTTPS (or localhost).
  useEffect(() => {
    if (!scannerVisible) return undefined;

    let stream = null;
    let rafId = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        // Downscale large camera frames so decoding stays fast
        const MAX_W = 640;
        const scale = Math.min(1, MAX_W / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(image.data, image.width, image.height, {
          inversionAttempts: "attemptBoth", // also reads light-on-dark QR codes
        });

        if (code && code.data) {
          handleBarcodeScanned(code.data);
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    const start = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const msg = window.isSecureContext
          ? "Camera is not supported in this browser."
          : "Camera needs HTTPS or localhost. Open this page over https:// (or use the IP field).";
        console.log("[Controller]", msg);
        setScanError(msg);
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const video = videoRef.current;
        if (!video) return;

        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        await video.play();

        rafId = requestAnimationFrame(tick);
      } catch (error) {
        console.log(
          "[Controller] Camera permission denied / unavailable:",
          error,
        );
        setScanError(
          error && error.name === "NotAllowedError"
            ? "Camera permission denied. Allow camera access in your browser settings."
            : `Could not start camera: ${error?.message || error}`,
        );
      }
    };

    start();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);

      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerVisible]);

  // ==========================================================
  // SEND
  // ==========================================================

  const send = (action, extra = {}) => {
    const message = { action, ...extra };

    console.log("[Controller] Sending:", JSON.stringify(message));

    // Room mode: emit on the room socket (server forwards it to the kiosk)
    if (connectionType === "room") {
      const roomSocket = socketRef.current;

      if (roomSocket && roomSocket.connected) {
        roomSocket.emit(ROOM_EVENT, message);
      } else {
        console.log("[Controller] Room socket not connected");
      }

      return;
    }

    const socket = socketRef.current;
    const target =
      connectionType === "electron"
        ? "Electron"
        : connectionType === "room"
          ? "Room"
          : "Android TV";

    if (socket && socket.connected) {
      try {
        socket.emit(EVENT_NAME, message);

        console.log(`[Controller] Sent to ${target}`);
      } catch (error) {
        console.log(`[Controller] ${target} send error:`, error);

        setConnected(false);
      }
    } else {
      console.log(`[Controller] ${target} not connected`);
    }
  };

  // ==========================================================
  // CLEANUP
  // ==========================================================

  useEffect(() => {
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================================
  // DEVICE INFO (shown on the Connect tab once connected)
  // ==========================================================

  const deviceId = room ? room.roomId : `${ip.trim()}:${PORT}`;
  const deviceName =
    connectionType === "room"
      ? "Room"
      : connectionType === "tv"
        ? "Android TV"
        : "Electron PC";

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="container">
      <div className="scroll-content">
        {/* LOGO */}
        <div className="logo-row">
          <span className="logo-mark">▲</span>
          <span className="logo-text">atttic</span>
        </div>

        {/* SEGMENTED BAR: status + 3 tabs */}
        <div className="segment-row">
          <button
            type="button"
            className={`tab-segment status-tab-segment ${
              tab === "connect" ? "tab-segment-active" : ""
            }`}
            onClick={() => setTab("connect")}
          >
            <span
              className={`status-dot ${
                connected ? "status-dot-on" : "status-dot-off"
              }`}
            />
            <span
              className={`segment-text ${
                tab === "connect" ? "segment-text-active" : ""
              }`}
            >
              {connected ? "Connected" : "Disconnected"}
            </span>
          </button>

          <button
            type="button"
            className={`tab-segment ${
              tab === "rotate" ? "tab-segment-active" : ""
            }`}
            onClick={() => setTab("rotate")}
          >
            <span
              className={`segment-text ${
                tab === "rotate" ? "segment-text-active" : ""
              }`}
            >
              Rotate
            </span>
          </button>

          <button
            type="button"
            className={`tab-segment tab-segment-last ${
              tab === "mouse" ? "tab-segment-active" : ""
            }`}
            onClick={() => setTab("mouse")}
          >
            <span
              className={`segment-text ${
                tab === "mouse" ? "segment-text-active" : ""
              }`}
            >
              Mouse
            </span>
          </button>
        </div>

        {/* CONNECT TAB */}
        {tab === "connect" && (
          <div className="tab-content">
            {!connected ? (
              <>
                <div className="connection-type-row">
                  {/* <button
                    type="button"
                    className={`small-toggle ${
                      connectionType === "electron" ? "small-toggle-active" : ""
                    }`}
                    onClick={() => setConnectionType("electron")}
                  >
                    Electron
                  </button> */}

                  <button
                    type="button"
                    className={`small-toggle ${
                      connectionType === "tv" ? "small-toggle-active" : ""
                    }`}
                    onClick={() => setConnectionType("tv")}
                  >
                    Android TV
                  </button>
                </div>

                {/* <input
                  className="input"
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  placeholder={
                    connectionType === "electron"
                      ? "Electron PC IP"
                      : "Android TV IP"
                  }
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="decimal"
                /> */}

                {connectionType !== "electron" && (
                  <button
                    type="button"
                    className="scan-link"
                    onClick={openScanner}
                  >
                    📷 Scan QR code instead
                  </button>
                )}

                {joinError && <div className="error-text">{joinError}</div>}

                <button
                  type="button"
                  className="primary-button"
                  onClick={connect}
                  disabled={joining}
                >
                  {joining ? "Joining room..." : "Connect"}
                </button>
              </>
            ) : (
              <>
                <div className="info-block">
                  <div className="info-label">Device Id</div>
                  <div className="info-value">{deviceId}</div>
                </div>

                <div className="info-block">
                  <div className="info-label">Device Name</div>
                  <div className="info-value">{deviceName}</div>
                </div>

                <button
                  type="button"
                  className="primary-button danger-button"
                  onClick={disconnect}
                >
                  Disconnect
                </button>
              </>
            )}
          </div>
        )}

        {/* ROTATE TAB — touchpad only */}
        {tab === "rotate" && (
          <div className="tab-content">
            <TouchpadView send={send} mode="rotate" />
          </div>
        )}

        {/* MOUSE TAB — touchpad + personalization */}
        {tab === "mouse" && (
          <div className="tab-content">
            <TouchpadView send={send} mode="mouse" />

            {/* <div className="personalise-label">Personalization Text</div>

            <div className="value-row">
              <input
                className="value-input"
                value={personaliseText}
                onChange={(e) =>
                  setPersonaliseText(e.target.value.toUpperCase())
                }
                placeholder="Enter engraving text"
                maxLength={10}
                autoCapitalize="characters"
              />

              <button
                type="button"
                className="save-button"
                onClick={() => send("typeText", { text: personaliseText })}
              >
                Save
              </button>
            </div> */}
          </div>
        )}
      </div>

      {/* QR SCANNER MODAL */}
      {scannerVisible && (
        <div className="scanner-container">
          <video ref={videoRef} className="camera" muted playsInline />
          <canvas ref={canvasRef} style={{ display: "none" }} />

          <div className="scanner-overlay">
            <div className="scanner-frame" />

            <div className="scanner-hint">
              {scanError || "Point the camera at the QR code on your TV"}
            </div>

            <button
              type="button"
              className="scanner-close-button"
              onClick={closeScanner}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
